import React, { FC, ReactElement, ReactNode, isValidElement } from 'react';
import cn from 'classnames';
import MediaQuery from 'react-responsive';
import { useTranslation } from 'react-i18next';
import { BaseButton, BaseButtonProps } from '../../../../../BaseComponents';
import { EngineTheme, useMergeClasses, useTheme } from '../../../../../theme';

import { MoneyFragment } from '@websky/graphql';

import { CdnIcon, CdnIconNames, IconTitle } from '@shared/ui';
import { Tip, TipProps } from '@shared/ui';
import { Money } from '../../../../../Money';
import { PassengerValueName } from '../../../../../types';
import { getFirstCharName, MOBILE_MIN_WIDTH } from '../../../../../utils';

type PassengerIconValue = number | PassengerValueName | CdnIconNames | ReactElement;

export interface PassTitleProps {
	classes?: EngineTheme['Entities']['Passenger']['PassTitle'];
	title: string;
	icon: PassengerIconValue;
	success?: boolean;
	error?: boolean;
	variant?: 'default' | 'added' | 'companion-offer' | 'add-to-companions' | 'accompanies';
	tipProps?: TipProps;
	disabled?: boolean;
	rightIcon?: ReactElement;
	fieldsMap?: Record<string, ReactNode>;
	money?: MoneyFragment;
	oldPrice?: MoneyFragment;
	headerButtonProps?: BaseButtonProps;
	onClear?: () => void;
	onAdd?: () => void;
}

const PassTitle: FC<PassTitleProps> = ({
	classes,
	title,
	icon,
	success,
	error,
	variant = 'default',
	headerButtonProps,
	tipProps,
	disabled,
	rightIcon,
	fieldsMap,
	money,
	oldPrice,
	onClear,
	onAdd
}) => {
	const { t } = useTranslation('Passengers');
	const defaultTheme = useTheme('Entities').Passenger.PassTitle;
	const theme = useMergeClasses(defaultTheme, classes);

	const getContent = (
		<div className={cn(theme.content, { [theme.fields]: fieldsMap })}>
			<div className={theme.header}>
				<div className={theme.titleWrapper}>
					<span className={theme.title}>{title}</span>

					{success && <CdnIcon icon="Info/Item Icluded" className={cn(theme.successIcon, theme.cdnIcon)} />}
				</div>

				{tipProps && <Tip variant="cheap" {...tipProps} />}
			</div>

			{headerButtonProps && (
				<BaseButton
					className={theme.headerButton}
					variant="text"
					children={t('Change accompanies')}
					{...headerButtonProps}
				/>
			)}

			{fieldsMap && (
				<div className={theme.fields}>
					{Object.entries(fieldsMap).map(([key, value]) => (
						<div key={key} className={theme.field}>
							{value}
						</div>
					))}
				</div>
			)}
		</div>
	);

	const iconContent = (
		<div
			className={cn(theme.iconWrapper, {
				[theme.errorColor]: error,
				[theme.disabled]: disabled,
				[theme.variantAddToCompanions]: variant === 'add-to-companions',
				[theme.variantAdded]: variant === 'added',
				[theme.variantCompanionOffer]: variant === 'companion-offer',
				[theme.variantAccompanies]: variant === 'accompanies'
			})}
		>
			{variant === 'add-to-companions' ? (
				<BaseButton onClick={onAdd} variant="text">
					<CdnIcon icon="Action/Add" className={theme.cdnIcon} />
				</BaseButton>
			) : typeof icon === 'number' ? (
				<span>{icon}</span>
			) : typeof icon === 'object' && icon !== null && !isValidElement(icon) ? (
				(() => {
					const passengerIcon = icon as PassengerValueName;

					return (
						<span>
							{getFirstCharName(
								{
									firstName: passengerIcon.firstName,
									lastName: passengerIcon.lastName,
									middleName: passengerIcon.middleName
								},
								'firstName lastName'
							)}
						</span>
					);
				})()
			) : typeof icon === 'string' ? (
				<CdnIcon icon={icon} className={theme.cdnIcon} />
			) : (
				icon
			)}
		</div>
	);

	const contentRight = rightIcon ? (
		<div className={cn(theme.rightIcon)}>{rightIcon}</div>
	) : money ? (
		<div className={theme.priceWrapper}>
			{oldPrice && <Money money={oldPrice} moneyClassName={theme.oldPrice} />}
			<Money money={money} moneyClassName={cn(theme.price, { [theme.price_withOldPrice]: oldPrice })} />
		</div>
	) : onClear ? (
		<MediaQuery minWidth={MOBILE_MIN_WIDTH + 1}>
			<BaseButton className={theme.clearButton} onClick={onClear} variant="text">
				{t('Clear')}
				<CdnIcon icon="Action/Close-Clear" className={theme.clearIcon} />
			</BaseButton>
		</MediaQuery>
	) : null;

	return (
		<div className={theme.root}>
			<IconTitle
				className={cn({
					[theme.disabled]: disabled,
					[theme.iconContainer]: fieldsMap
				})}
				content={getContent}
				mainIcon={iconContent}
				contentRight={contentRight}
			/>
		</div>
	);
};

export default PassTitle;
